/*
 * Quote Pricing Engine — Iter 333 (Option A: Correctness First)
 *
 * Pure, side-effect-free pricing math for the Quote Builder. Ported from
 * the reference `ariaone-quote-proposal-mockup.html` per the Dev Handoff
 * spec (`quote-builder-dev-handoff.html`).
 *
 * Every line item has TWO shape-defining fields:
 *   - basis: billing cadence & math (fixed/annual/onetime/perunit/pmpm/pctcoll/pctreimb)
 *   - kind:  product shape (base/seat/feature/license/program)
 *
 * Totals live in FOUR SEPARATE BUCKETS — never combined:
 *   - fixedMonthly   → $ per month, recurring
 *   - volumeMonthly  → $ per month, estimated (transaction × rate, patient × rate)
 *   - annual         → $ per year, billed annually (never in monthly)
 *   - oneTime        → $ one-time fees (never in monthly)
 *
 * Percentage lines quote a RATE only ("6% of collections"), never a dollar,
 * and are surfaced separately as `rateLines[]`.
 *
 * This module is intentionally framework-free so it can be unit-tested from
 * plain Node and reused for a future backend PDF generator.
 */

export const BASIS = Object.freeze({
  FIXED:    'fixed',
  ANNUAL:   'annual',
  ONETIME:  'onetime',
  PERUNIT:  'perunit',
  PMPM:     'pmpm',
  PCTCOLL:  'pctcoll',
  PCTREIMB: 'pctreimb',
});

export const KIND = Object.freeze({
  BASE:    'base',
  SEAT:    'seat',
  FEATURE: 'feature',
  LICENSE: 'license',
  PROGRAM: 'program',
});

// Spec §4 — the 5 SKUs mislabeled as `% of collections` in the live
// catalog are actually `% of reimbursement`. Different approval floors,
// different print language. Enforce here.
export const PCT_REIMB_SKUS = new Set([
  'CCM — Percentage',
  'APCM — Percentage',
  'AWV — Percentage',
  'RPM — Percentage (recouped)',
  'RPM — Percentage (device upfront)',
]);

// Legacy shim — infers `basis` from a catalog/line-item's `unit` +
// `pricing_model` fields. Used at load time so quotes saved before
// Iter 333 still price correctly.
export function inferBasis(item) {
  const name = String(item.product || item.name || '');
  if (PCT_REIMB_SKUS.has(name)) return BASIS.PCTREIMB;

  const unit  = String(item.unit_label || item.unit || '').toLowerCase();
  const model = String(item.pricing_model || item.model || '').toLowerCase();

  if (model === 'percentage') {
    if (unit.includes('reimbursement') || unit.includes('reimb')) return BASIS.PCTREIMB;
    return BASIS.PCTCOLL;
  }
  if (/\/yr\b|per\s*year|annual/.test(unit)) return BASIS.ANNUAL;
  if (/one-time|onetime|\bupfront\b/.test(unit)) return BASIS.ONETIME;
  if (/\/transaction|\/check|\/authorization|\/auth\b/.test(unit)) return BASIS.PERUNIT;
  if (/\/patient/.test(unit)) return BASIS.PMPM;

  return BASIS.FIXED;
}

export function normalizeLegacy(raw) {
  if (!raw) return raw;
  if (raw.basis) return raw;
  return { ...raw, basis: inferBasis(raw) };
}

// Graduated tier math (§8 — mockup verified 150k txns @ [$0.25 up to 100k,
// $0.20 above] = $35,000 not $30,000 flat-rate and not $37,500 flat-hi-tier).
function applyTiers(volume, defaultUnit, tiers) {
  if (!Array.isArray(tiers) || tiers.length === 0) return volume * defaultUnit;
  const sorted = [...tiers].sort((a, b) => (a.upTo ?? Infinity) - (b.upTo ?? Infinity));
  let remaining = volume;
  let total = 0;
  let prev = 0;
  for (const tier of sorted) {
    const cap = tier.upTo == null ? Infinity : tier.upTo;
    const window = Math.min(remaining, cap - prev);
    if (window <= 0) { prev = cap; continue; }
    total += window * Number(tier.rate ?? defaultUnit);
    remaining -= window;
    prev = cap;
    if (remaining <= 0) break;
  }
  if (remaining > 0) total += remaining * defaultUnit;
  return total;
}

/**
 * priceOf(item) — pure pricing for one line item.
 *
 * Returns one of:
 *   { kind: 'money', bucket: 'fixedMonthly'|'volumeMonthly'|'annual'|'oneTime', net, minApplied? }
 *   { kind: 'rate',  bucket: 'rate', rate, of: 'collections'|'reimbursement' }
 */
export function priceOf(rawItem) {
  const item = normalizeLegacy(rawItem);
  const basis = item.basis;
  const qty = Number(item.qty ?? item.quantity ?? 1) || 0;
  const unit = Number(item.unit_price ?? item.price ?? 0) || 0;

  switch (basis) {
    case BASIS.FIXED:
      return { kind: 'money', bucket: 'fixedMonthly', net: qty * unit };
    case BASIS.ANNUAL:
      return { kind: 'money', bucket: 'annual', net: qty * unit };
    case BASIS.ONETIME:
      return { kind: 'money', bucket: 'oneTime', net: qty * unit };
    case BASIS.PERUNIT: {
      const volume = Number(item.vol ?? item.volume ?? qty) || 0;
      const gross = applyTiers(volume, unit, item.tiers);
      const min = Number(item.min ?? 0) || 0;
      const net = Math.max(gross, min);
      return { kind: 'money', bucket: 'volumeMonthly', net, minApplied: net > gross };
    }
    case BASIS.PMPM: {
      const patients = Number(item.patients ?? qty) || 0;
      const gross = patients * unit;
      const min = Number(item.min ?? 0) || 0;
      const net = Math.max(gross, min);
      return { kind: 'money', bucket: 'volumeMonthly', net, minApplied: net > gross };
    }
    case BASIS.PCTCOLL:
      return { kind: 'rate', bucket: 'rate', rate: unit, of: 'collections' };
    case BASIS.PCTREIMB:
      return { kind: 'rate', bucket: 'rate', rate: unit, of: 'reimbursement' };
    default:
      // Unknown basis — safest fallback is fixedMonthly at qty*unit
      return { kind: 'money', bucket: 'fixedMonthly', net: qty * unit };
  }
}

/**
 * totals(items) — bucket sums + rate lines for a whole quote.
 *
 * Discount rows (`setup_discount`, `product_discount`) are skipped here;
 * they're modifiers handled by the existing `quoteBuilderHelpers`
 * discount reducer. Phase-2 concern per the spec.
 */
export function totals(items = []) {
  const buckets = { fixedMonthly: 0, volumeMonthly: 0, annual: 0, oneTime: 0 };
  const rateLines = [];
  for (const raw of items) {
    if (!raw) continue;
    const model = String(raw.pricing_model || raw.model || '').toLowerCase();
    if (model === 'setup_discount' || model === 'product_discount') continue;

    const p = priceOf(raw);
    if (p.kind === 'money') {
      buckets[p.bucket] = (buckets[p.bucket] || 0) + (p.net || 0);
    } else {
      rateLines.push({
        rate: p.rate,
        of: p.of,
        product: raw.product || raw.name || '(unnamed)',
      });
    }
  }
  return { ...buckets, rateLines };
}

/**
 * True monthly recurring revenue (fixed + est. volume). This is the
 * headline number reps see and is what should populate `quote.subtotal`
 * / `quote.total` in the persistence payload.
 *
 * Never includes annual or one-time fees.
 */
export function trueMRR(items = []) {
  const t = totals(items);
  return (t.fixedMonthly || 0) + (t.volumeMonthly || 0);
}

/**
 * Whether the quote has any rate lines. When true, the "Annual estimate"
 * headline should be suppressed per spec §4 — because the annual
 * figure would be misleading without knowing collections/reimbursement.
 */
export function hasRateLines(items = []) {
  return totals(items).rateLines.length > 0;
}

/**
 * Plain-English summary sentence for the sticky bar and right panel.
 * Matches the mockup's `renderSummary()` cadence:
 *   "$945/mo estimated, plus 6% of collections and 4% of reimbursement · $250/yr annual · $2,500 one-time"
 *
 * Rules (spec + mockup):
 *   - Rate lines are joined with the natural connectors "plus X" then "and Y".
 *   - Annual and one-time are separated by ` · ` (interpunct).
 *   - "annual" and "one-time" are single-word suffixes; no /yr on the annual
 *     tag itself when in the sentence (the dollar value already carries /yr).
 */
export function summarySentence(items = []) {
  const t = totals(items);
  const mrr = (t.fixedMonthly || 0) + (t.volumeMonthly || 0);
  const parts = [];

  if (mrr > 0) {
    parts.push(`$${mrr.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo estimated`);
  }

  if (t.rateLines.length > 0) {
    const rateBits = t.rateLines.map(r => `${r.rate}% of ${r.of}`);
    let rateStr;
    if (rateBits.length === 1) rateStr = `plus ${rateBits[0]}`;
    else if (rateBits.length === 2) rateStr = `plus ${rateBits[0]} and ${rateBits[1]}`;
    else rateStr = `plus ${rateBits.slice(0, -1).join(', ')} and ${rateBits[rateBits.length - 1]}`;
    // Attach with comma if we have an MRR head, else stand alone.
    if (parts.length > 0) parts[0] = parts[0] + ', ' + rateStr;
    else parts.push(rateStr.charAt(0).toUpperCase() + rateStr.slice(1));
  }

  if (t.annual > 0) parts.push(`$${t.annual.toLocaleString(undefined, { maximumFractionDigits: 0 })}/yr annual`);
  if (t.oneTime > 0) parts.push(`$${t.oneTime.toLocaleString(undefined, { maximumFractionDigits: 0 })} one-time`);

  return parts.join(' · ') || 'Empty quote';
}

// Human-readable basis label used in the line-item table + PDF
export function basisLabel(basis) {
  return ({
    [BASIS.FIXED]:    '/mo',
    [BASIS.ANNUAL]:   '/yr',
    [BASIS.ONETIME]:  'one-time',
    [BASIS.PERUNIT]:  '/transaction',
    [BASIS.PMPM]:     '/patient/mo',
    [BASIS.PCTCOLL]:  '% of collections',
    [BASIS.PCTREIMB]: '% of reimbursement',
  })[basis] || '';
}

// Which bucket each basis lands in — used for line-item chip color coding.
export function bucketOfBasis(basis) {
  return ({
    [BASIS.FIXED]:    'fixedMonthly',
    [BASIS.PMPM]:     'volumeMonthly',
    [BASIS.PERUNIT]:  'volumeMonthly',
    [BASIS.ANNUAL]:   'annual',
    [BASIS.ONETIME]:  'oneTime',
    [BASIS.PCTCOLL]:  'rate',
    [BASIS.PCTREIMB]: 'rate',
  })[basis] || 'fixedMonthly';
}
